import { Component, h, Element, Prop, Build, Host } from '@stencil/core';
import { GoogleIcon } from '../shared/google-icon';

/**
 * Social authentication component for SignNow (Google Sign up button)
 */

// Environment constants
const PRODUCTION_DOMAIN = '.signnow.com';
const DEVELOPMENT_DOMAIN = '.signnow-dev.xyz';

// Helper to detect production environment based on hostname
const isProduction = () => {
  if (typeof window === 'undefined') return false;
  const hostname = window.location.hostname;
  return hostname.includes('signnow.com') || hostname.includes('mrkhub.com');
};

// Helper to detect mobile devices (desktop only component)
const isMobile = () => {
  if (typeof window === 'undefined') return false;
  return window.innerWidth < 960;
};

// Helper functions
const getAuthUrl = (pathSuffix = '') => {
  const baseUrl = isProduction() ? `https://app${PRODUCTION_DOMAIN}` : `https://app3${DEVELOPMENT_DOMAIN}`;
  return `${baseUrl}${pathSuffix}`;
};

const getDomain = () => (isProduction() ? PRODUCTION_DOMAIN : DEVELOPMENT_DOMAIN);

const getCookie = (name: string) => {
  const prefix = `${name}=`;
  let begin = document.cookie.indexOf(`; ${prefix}`);
  let end = null;

  if (begin === -1) {
    begin = document.cookie.indexOf(prefix);
    if (begin !== 0) return null;
  } else {
    begin += 2;
    end = document.cookie.indexOf(';', begin);
    if (end === -1) end = document.cookie.length;
  }

  return decodeURI(document.cookie.substring(begin + prefix.length, end));
};

const setCookie = (name: string, value: string, options: { expires?: number; domain?: string; path?: string } = {}) => {
  let cookieString = `${name}=${value}`;

  if (options.expires) {
    const date = new Date();
    date.setTime(date.getTime() + options.expires * 24 * 60 * 60 * 1000);
    cookieString += `; expires=${date.toUTCString()}`;
  }

  if (options.domain) {
    cookieString += `; domain=${options.domain}`;
  }

  if (options.path) {
    cookieString += `; path=${options.path}`;
  }

  document.cookie = cookieString;
};

const removeCookie = (name: string, options: { domain?: string; path?: string } = {}) => {
  setCookie(name, '', { ...options, expires: -1 });
};

const generateXSRFToken = () => {
  const domain = getDomain();
  const currentDate = new Date().getTime();
  const string = currentDate.toString();
  const { length } = string;

  let i = 0;
  let state = 0;

  if (length > 0) {
    while (i < length) {
      state = ((state << 5) - state + string.charCodeAt(i++)) | 0;
    }
    state = Math.abs(state);
  }

  const tokenValue = state.toString();

  setCookie('SN_SOCIAL_AUTH_XSRF_TOKEN', tokenValue, {
    expires: 30,
    domain,
    path: '/',
  });

  return tokenValue;
};

const getProviderFromButton = (button: HTMLElement) => {
  const pathDomain = getDomain();

  removeCookie('XSRF-TOKEN', { domain: pathDomain, path: '/' });

  const spanText = button.querySelector('span')?.textContent;

  if (spanText === 'Facebook') {
    console.log('[Analytics] Facebook Start');
    return 'facebook';
  } else if (spanText === 'Google') {
    console.log('[Analytics] Google Start');
    return 'google';
  } else if (spanText === 'Microsoft') {
    console.log('[Analytics] Microsoft Start');
    return 'microsoft';
  }

  return 'google'; // Default to Google
};

/**
 * Social authentication component for SignNow (Google Sign up button).
 *
 * @product signnow
 * @source social-auth
 * @version 2.0.0
 * @category SignNow
 * @example
 *   <sn-social-auth-type-1
 *     button-text="Sign up with Google"
 *   ></sn-social-auth-type-1>
 */
@Component({
  tag: 'sn-social-auth-type-1',
  styleUrl: 'index.scss',
  shadow: false,
})
export class SNSocialAuthType1 {
  @Element() el!: HTMLElement;

  @Prop() colorMode: 'signnow' | 'inverted' = 'signnow';

  /**
   * Button label text.
   * @content
   * @dynamic
   * @default "Sign up with Google"
   */
  @Prop({ attribute: 'button-text' }) buttonText: string = 'Sign up with Google';

  private buttonElement?: HTMLElement;
  private boundHandleClick?: EventListener;
  private boundHandlePageShow?: EventListener;

  componentDidLoad() {
    if (!Build.isBrowser) return;

    // Desktop only - skip on mobile devices
    if (isMobile()) return;

    this.buttonElement = this.el.querySelector('.js-sign-button') as HTMLElement;

    this.boundHandleClick = this.handleButtonClick.bind(this);
    if (this.buttonElement) {
      this.buttonElement.addEventListener('click', this.boundHandleClick);
    }

    // When navigating back from Google OAuth, the browser may restore the page
    // from bfcache with the 'is-hidden' class still applied — remove it.
    this.boundHandlePageShow = (e: Event) => {
      if ((e as PageTransitionEvent).persisted) {
        this.el.querySelector('.js-sign-button')?.classList.remove('is-hidden');
      }
    };
    window.addEventListener('pageshow', this.boundHandlePageShow);
  }

  disconnectedCallback() {
    if (!Build.isBrowser) return;

    if (this.buttonElement && this.boundHandleClick) {
      this.buttonElement.removeEventListener('click', this.boundHandleClick);
    }
    if (this.boundHandlePageShow) {
      window.removeEventListener('pageshow', this.boundHandlePageShow);
    }
  }

  private async handleButtonClick(e: Event) {
    e.preventDefault();

    const button = e.currentTarget as HTMLElement;

    // Set social auth origin cookie
    if (!getCookie('social_auth_origin')) {
      setCookie('social_auth_origin', 'landing', {
        domain: getDomain(),
        path: '/',
      });
    }

    const path = getAuthUrl('/auth/v1');
    const provider = getProviderFromButton(button);
    const state = getCookie('SN_SOCIAL_AUTH_XSRF_TOKEN') || generateXSRFToken();

    try {
      const response = await fetch(`${path}/socialite/${provider}/link?state=${state}`, {
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      // Hide Google button and redirect
      this.hideGoogleButton();
      if (Build.isBrowser) {
        window.location.href = data.link;
      }
    } catch (error) {
      console.error('Social auth error:', error);
    }
  }

  private hideGoogleButton() {
    if (!Build.isBrowser) return;

    const googleButton = this.el.querySelector('.js-sign-button');
    if (googleButton) {
      googleButton.classList.add('is-hidden');
    }
  }

  render() {
    // Skip SSR - requires browser APIs for authentication
    if (!Build.isBrowser) {
      return <Host data-color-mode={this.colorMode}></Host>;
    }

    // Desktop only - don't render on mobile devices
    if (isMobile()) {
      return <Host data-color-mode={this.colorMode}></Host>;
    }

    return (
      <Host data-color-mode={this.colorMode}>
        <div class="google-button js-sign-button">
          <GoogleIcon />
          <div class="google-button__text">{this.buttonText || this.el.getAttribute('buttontext') || this.el.getAttribute('button-text') || 'Sign up with Google'}</div>
        </div>
      </Host>
    );
  }
}
